import React from 'react'
import { toast } from 'react-toastify'
import * as yup from 'yup'
import { useFormik } from 'formik'
import { Moment } from 'moment'
import { useIntl } from 'react-intl'
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormHelperText,
} from '@mui/material'
import { H6 } from '../../../../../../layouts/TextStyles'
import CustomInput from '../../../../../../components/muiComponents/customInput/CustomInput'
import { Lorum } from '../../../../../../utils/constants'
import DatePicker from '../../../../../../components/muiComponents/datePicker/DatePicker'
import { CoursePromotionState } from '../../../../../../features/types'
import {
    isNumber,
    isRequiredDate,
    requiredString,
} from '../../../../../../utils/validation'
import { coursePromotionApi } from '../../../../../../features/course/coursePromotion'

interface Props {
    editPromo?: CoursePromotionState
    coursePriceId: number
    open: boolean
    handleClose: () => void
}

const styles = {
    error: {
        color: '#d32f2f',
        marginLeft: 2,
    },
    fullWidth: {
        width: '100%',
    },
    flex: {
        display: 'flex',
    },
}

const PromoDialog: React.FC<Props> = ({
    editPromo = null,
    coursePriceId,
    open,
    handleClose,
}) => {
    const { formatMessage } = useIntl()
    const [createPromotion, { isLoading: isCreating }] =
        coursePromotionApi.useCreateCoursePromotionMutation()
    const [updatePromotion, { isLoading: isUpdating }] =
        coursePromotionApi.useUpdateCoursePromotionMutation()

    const isLoading = isCreating || isUpdating
    const promotion: CoursePromotionState = {
        id: editPromo?.id ?? null,
        sloganKz: editPromo?.sloganKz ?? '',
        sloganRu: editPromo?.sloganRu ?? '',
        startTime: editPromo?.startTime ?? null,
        endTime: editPromo?.endTime ?? null,
        coursePriceId: coursePriceId,
        discount: editPromo?.discount ?? null,
    }

    const validationSchema = yup.object().shape({
        sloganKz: requiredString({ formatMessage }),
        sloganRu: requiredString({ formatMessage }),
        startTime: isRequiredDate({ formatMessage }),
        endTime: isRequiredDate({ formatMessage }),
        discount: isNumber({ formatMessage }),
    })

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: promotion,
        validationSchema: validationSchema,
        validateOnChange: false,
        validateOnBlur: false,

        onSubmit: async () => {
            if (editPromo) {
                handleUpdatePromotion()
            } else {
                handleCreatePromotion()
            }
        },
    })

    const handleCreatePromotion = async () => {
        try {
            await createPromotion(formik.values).unwrap()
            formik.resetForm()
            toast.success(formatMessage({ id: 'savedSuccess' }))
            handleClose()
        } catch (error) {}
    }

    const handleUpdatePromotion = async () => {
        try {
            await updatePromotion(formik.values).unwrap()
            toast.success(formatMessage({ id: 'updatedSuccess' }))
            handleClose()
        } catch (error) {}
    }

    const handleDateChange = async (key: string, event: Moment) => {
        formik.setFieldValue(key, event.add(6, 'hours'))
    }

    return (
        <Dialog
            maxWidth={'md'}
            fullWidth
            open={open}
            onClose={handleClose}
        >
            <DialogTitle>{formatMessage({ id: 'promotion' })}</DialogTitle>

            <DialogContent>
                <form onSubmit={formik.handleSubmit}>
                    <Box sx={styles.flex}>
                        <Box
                            sx={styles.fullWidth}
                            mr={4}
                        >
                            <Box mb={2}>
                                <H6>{formatMessage({ id: 'discount%' })}</H6>

                                <CustomInput
                                    id="discount"
                                    name="discount"
                                    value={formik.values.discount}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={Boolean(formik.errors.discount)}
                                    helperText={formik.errors.discount}
                                    placeholder={formatMessage({
                                        id: 'percent',
                                    })}
                                    fullWidth
                                />
                            </Box>

                            <Box mb={2}>
                                <H6>{formatMessage({ id: 'sloganKz' })}</H6>

                                <CustomInput
                                    id="sloganKz"
                                    name="sloganKz"
                                    value={formik.values.sloganKz}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={Boolean(formik.errors.sloganKz)}
                                    helperText={formik.errors.sloganKz}
                                    placeholder={Lorum}
                                    fullWidth
                                    multiline
                                    maxRows={5}
                                    minRows={5}
                                />
                            </Box>
                        </Box>

                        <Box sx={styles.fullWidth}>
                            <Box mb={2}>
                                <H6>
                                    {formatMessage({ id: 'start&endTime' })}
                                </H6>

                                <Box
                                    sx={styles.flex}
                                    mt={2}
                                >
                                    <Box
                                        mr={2}
                                        mb={1}
                                    >
                                        <DatePicker
                                            id="startTime"
                                            name="startTime"
                                            label={formatMessage({
                                                id: 'startDate',
                                            })}
                                            value={formik.values.startTime}
                                            handleChange={(event) =>
                                                handleDateChange(
                                                    'startTime',
                                                    event,
                                                )
                                            }
                                            handleError={Boolean(
                                                formik.errors.startTime,
                                            )}
                                            handleBlur={formik.handleBlur}
                                        />

                                        <FormHelperText sx={styles.error}>
                                            {formik.submitCount &&
                                            !formik.values.startTime
                                                ? formatMessage({
                                                      id: 'isRequired',
                                                  })
                                                : null}
                                        </FormHelperText>
                                    </Box>

                                    <Box>
                                        <DatePicker
                                            id="endTime"
                                            name="endTime"
                                            label={formatMessage({
                                                id: 'endDate',
                                            })}
                                            value={formik.values.endTime}
                                            handleChange={(event) =>
                                                handleDateChange(
                                                    'endTime',
                                                    event,
                                                )
                                            }
                                            handleError={Boolean(
                                                formik.errors.endTime,
                                            )}
                                            handleBlur={formik.handleBlur}
                                        />

                                        <FormHelperText sx={styles.error}>
                                            {formik.submitCount &&
                                            !formik.values.endTime
                                                ? formatMessage({
                                                      id: 'isRequired',
                                                  })
                                                : null}
                                        </FormHelperText>
                                    </Box>
                                </Box>
                            </Box>

                            <Box mb={2}>
                                <H6>{formatMessage({ id: 'sloganRu' })}</H6>

                                <CustomInput
                                    id="sloganRu"
                                    name="sloganRu"
                                    value={formik.values.sloganRu}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={Boolean(formik.errors.sloganRu)}
                                    helperText={formik.errors.sloganRu}
                                    placeholder={Lorum}
                                    fullWidth
                                    multiline
                                    maxRows={5}
                                    minRows={5}
                                />
                            </Box>
                        </Box>
                    </Box>

                    <DialogActions>
                        <Button
                            onClick={() => formik.handleSubmit()}
                            variant="contained"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <CircularProgress
                                    size={20}
                                    style={{ marginRight: '8px' }}
                                />
                            ) : editPromo ? (
                                formatMessage({ id: 'update' })
                            ) : (
                                formatMessage({ id: 'add' })
                            )}
                        </Button>
                    </DialogActions>
                </form>
            </DialogContent>
        </Dialog>
    )
}

export default PromoDialog
